import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { NestedPick, Paginated, PaginationQuery, PaginationQueryWithFields, Version, Versions } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AssignNewSubsidiary,
  CarSubsidiary,
  CarSubsidiaryNoCarDepency,
  CarSubsidiaryVersion,
  InsertCarSubsidiary,
  OpenEntities,
  UpdateCarSubsidiary,
} from "./types";

export const useCarSubsidiariesApi = () => {
  const api = useApi();

  const getCarSubsidiary = useCallback(
    (carSubsidiaryId: string, signal: AbortSignal) => {
      const url = `/admin/car_subsidiaries/${carSubsidiaryId}`;
      return useSWRMutation<CarSubsidiary | null, CustomError, string>(url, (url) =>
        api.get<CarSubsidiary>({ url, signal }),
      );
    },
    [api],
  );

  const useCarSubsidiaries = useCallback(
    <K extends keyof CarSubsidiaryNoCarDepency>(
      query: PaginationQueryWithFields<NestedPick<CarSubsidiaryNoCarDepency, K>>,
      signal: AbortSignal,
      carId?: string,
    ) => {
      const url = carId
        ? buildUrl<NestedPick<CarSubsidiaryNoCarDepency, K>>(`/admin/cars/${carId}/subsidiaries`, query)
        : null;
      return useSWR<Paginated<NestedPick<CarSubsidiaryNoCarDepency, K>> | null, CustomError, string | null>(
        url,
        (url) => (url ? api.get<Paginated<NestedPick<CarSubsidiaryNoCarDepency, K>>>({ url, signal }) : null),
      );
    },
    [api],
  );

  const updateCarSubsidiary = useCallback(
    async (carSubsidiaryId: string, body: UpdateCarSubsidiary, carId: string) => {
      const url = `/admin/car_subsidiaries/${carSubsidiaryId}`;
      await api.put<UpdateCarSubsidiary>(url, body);
      api.mutateCache([
        url,
        `/admin/cars`,
        `/admin/cars/${carId}`,
        `/admin/cars/${carId}/subsidiaries`,
        `/admin/cars/${carId}/overview`,
        `/admin/cars/${carId}/tasks`,
        `/admin/car_tasks`,
      ]);
    },
    [api],
  );

  const getCarSubsidiariesHistory = useCallback(
    (query: PaginationQuery, carSubsidiaryID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/car_subsidiaries/${carSubsidiaryID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getCarSubsidiariesVersion = useCallback(
    (carSubsidiaryId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/car_subsidiaries/${carSubsidiaryId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<CarSubsidiaryVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<CarSubsidiaryVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const deleteCarSubsidiary = useCallback(
    async (carSubsidiaryId: string, carId: string): Promise<void> => {
      const url = `/admin/car_subsidiaries/${carSubsidiaryId}`;
      await api.del(url).then(() => {
        api.mutateCache([
          url,
          `/admin/cars`,
          `/admin/car_subsidiaries`,
          `/admin/cars/${carId}`,
          `/admin/cars/${carId}/subsidiaries`,
          `/admin/cars/${carId}/overview`,
        ]);
      });
    },
    [api],
  );

  const upsertCarSubsidiary = useCallback(
    async (body: AssignNewSubsidiary, carId: string) => {
      const url = `/admin/cars/${carId}/assign_new_subsidiary`;
      await api.post<AssignNewSubsidiary, { id: string }>(url, body);
      api.mutateCache([
        url,
        `/admin/cars`,
        `/admin/cars/${carId}`,
        `/admin/cars/${carId}/subsidiaries`,
        `/admin/cars/${carId}/overview`,
      ]);
    },
    [api],
  );

  const insertCarSubsidiary = useCallback(
    async (body: InsertCarSubsidiary, carId: string) => {
      const url = `/admin/car_subsidiaries`;
      await api.post<InsertCarSubsidiary, { id: string }>(url, body);
      api.mutateCache([url, `/admin/cars`, `/admin/cars/${carId}/subsidiaries`]);
    },
    [api],
  );

  const getOpenEntities = useCallback(
    (carSubsidiaryId: string, endDate: string, signal: AbortSignal) => {
      const url = `/admin/car_subsidiaries/${carSubsidiaryId}/open_entities?end_date=${endDate}`;
      return api.get<OpenEntities>({ url, signal });
    },
    [api],
  );

  return {
    useCarSubsidiaries,
    getCarSubsidiary,
    getCarSubsidiariesHistory,
    getCarSubsidiariesVersion,
    updateCarSubsidiary,
    deleteCarSubsidiary,
    upsertCarSubsidiary,
    insertCarSubsidiary,
    getOpenEntities,
  };
};
